import { useMemo, useState } from "react";
import { Loader2, UserCheck } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
	SearchableSelect,
	type SearchableSelectOption,
} from "@/components/ui/searchable-select";
import { UserMultiSelect } from "../UserMultiSelect";
import { useAuth } from "@/context/AuthContext";
import { type Profile } from "@/services/profile.service";
import { type UiTask, getInitials, profileColorClass } from "../types";
import { UNASSIGNED_VALUE } from "./constants";
import { reportError } from "./utils";

export function InlineAssignee({
	assignee,
	additionalAssignees,
	profiles,
	canEdit,
	onSave,
	onSaveAdditional,
}: {
	assignee: UiTask["assigned_to"];
	additionalAssignees: UiTask["additional_assignees"];
	profiles: Profile[];
	canEdit: boolean;
	onSave: (v: string | null) => Promise<void>;
	onSaveAdditional: (ids: string[]) => Promise<void>;
}) {
	const { user } = useAuth();
	const [editing, setEditing] = useState(false);
	const [saving, setSaving] = useState(false);
	const [editingAdditional, setEditingAdditional] = useState(false);
	const [savingAdditional, setSavingAdditional] = useState(false);
	const [pendingAdditionalIds, setPendingAdditionalIds] = useState<string[]>(
		[],
	);
	const currentId = assignee?.id ?? "";
	const additionalIds = additionalAssignees.map((p) => p.id);
	const isMe = !!user && user.id === currentId;
	const canAssignMe =
		canEdit && !!user && !isMe && profiles.some((p) => p.id === user.id);

	async function pick(v: string) {
		const next = v === UNASSIGNED_VALUE ? null : v;
		if ((next ?? "") === currentId) {
			setEditing(false);
			return;
		}
		setSaving(true);
		try {
			await onSave(next);
		} catch (e) {
			reportError(e);
		} finally {
			setSaving(false);
			setEditing(false);
		}
	}

	async function assignToMe(e: React.MouseEvent) {
		e.stopPropagation();
		if (!user || saving) return;
		setSaving(true);
		try {
			await onSave(user.id);
		} catch (err) {
			reportError(err);
		} finally {
			setSaving(false);
		}
	}

	const options: SearchableSelectOption[] = useMemo(
		() => [
			{ value: UNASSIGNED_VALUE, label: "Unassigned" },
			...profiles.map((p) => ({
				value: p.id,
				label: p.full_name ?? p.email,
				description: p.full_name ? p.email : undefined,
			})),
		],
		[profiles],
	);

	const additionalProfiles = useMemo(
		() => profiles.filter((p) => p.id !== currentId),
		[profiles, currentId],
	);

	function startEditingAdditional() {
		setPendingAdditionalIds(additionalIds);
		setEditingAdditional(true);
	}

	async function commitAdditional(ids: string[]) {
		setEditingAdditional(false);
		const changed =
			ids.length !== additionalIds.length ||
			ids.some((id) => !additionalIds.includes(id));
		if (!changed) return;
		setSavingAdditional(true);
		try {
			await onSaveAdditional(ids);
		} catch (e) {
			reportError(e);
		} finally {
			setSavingAdditional(false);
		}
	}

	const additionalReadView =
		additionalAssignees.length > 0 ? (
			<div className="flex items-center flex-wrap gap-1.5">
				{additionalAssignees.map((p) => (
					<div key={p.id} className="flex items-center gap-1.5">
						<Avatar className="h-6 w-6">
							{p.avatar_url ? (
								<AvatarImage
									src={p.avatar_url}
									alt={p.full_name ?? p.email}
								/>
							) : null}
							<AvatarFallback
								className={`text-[9px] text-white ${profileColorClass(p.id)}`}
							>
								{getInitials(p.full_name ?? p.email)}
							</AvatarFallback>
						</Avatar>
						<span className="text-xs text-foreground truncate">
							{p.full_name ?? p.email}
						</span>
					</div>
				))}
			</div>
		) : (
			<p className="text-xs text-muted">No additional assignees</p>
		);

	const additionalSection = canEdit ? (
		<div className="mt-2">
			<p className="text-[11px] font-medium text-muted mb-1">
				Additional assignees
			</p>
			{editingAdditional ? (
				<div className="flex items-center gap-2">
					<div className="flex-1 min-w-0">
						<UserMultiSelect
							size="sm"
							autoFocus
							defaultOpen
							value={pendingAdditionalIds}
							onValueChange={setPendingAdditionalIds}
							onOpenChange={(open) => {
								if (!open) commitAdditional(pendingAdditionalIds);
							}}
							profiles={additionalProfiles}
							placeholder="Add assignees…"
							disabled={savingAdditional}
						/>
					</div>
					{savingAdditional && (
						<Loader2 className="h-3.5 w-3.5 animate-spin text-muted" />
					)}
				</div>
			) : (
				<div className="flex items-center gap-2">
					<button
						type="button"
						onClick={startEditingAdditional}
						disabled={savingAdditional}
						className="flex-1 min-w-0 text-left rounded -mx-1 px-1 py-0.5 hover:bg-muted-subtle/60 transition-colors disabled:opacity-60"
					>
						{additionalReadView}
					</button>
					{savingAdditional && (
						<Loader2 className="h-3.5 w-3.5 animate-spin text-muted shrink-0" />
					)}
				</div>
			)}
		</div>
	) : (
		<div className="mt-2">
			<p className="text-[11px] font-medium text-muted mb-1">
				Additional assignees
			</p>
			{additionalReadView}
		</div>
	);

	const readView = assignee ? (
		<div className="flex items-center gap-2">
			<Avatar className="h-8 w-8">
				{assignee.avatar_url ? (
					<AvatarImage
						src={assignee.avatar_url}
						alt={assignee.full_name ?? assignee.email}
					/>
				) : null}
				<AvatarFallback
					className={`text-[10px] text-white ${profileColorClass(assignee.id)}`}
				>
					{getInitials(assignee.full_name ?? assignee.email)}
				</AvatarFallback>
			</Avatar>
			<span className="text-sm font-medium text-foreground truncate">
				{assignee.full_name ?? assignee.email}
			</span>
		</div>
	) : (
		<p className="text-sm text-muted">Unassigned</p>
	);

	if (!canEdit) {
		return (
			<div>
				{readView}
				{additionalSection}
			</div>
		);
	}

	if (!editing) {
		return (
			<div>
				<div className="flex items-center gap-2">
					<button
						type="button"
						onClick={() => setEditing(true)}
						className="flex items-center gap-2 flex-1 min-w-0 text-left cursor-pointer rounded -mx-1 px-1 py-0.5 hover:bg-muted-subtle/60 transition-colors"
					>
						{readView}
					</button>
					{canAssignMe && (
						<button
							type="button"
							onClick={assignToMe}
							disabled={saving}
							title="Assign to me"
							className="inline-flex items-center gap-1 text-[11px] font-medium text-primary hover:bg-primary/10 px-1.5 py-0.5 rounded transition-colors disabled:opacity-50 shrink-0"
						>
							{saving ? (
								<Loader2 className="h-3 w-3 animate-spin" />
							) : (
								<UserCheck className="h-3 w-3" />
							)}
							Me
						</button>
					)}
				</div>
				{additionalSection}
			</div>
		);
	}

	return (
		<div>
			<div className="flex items-center gap-2 flex-1 min-w-0">
				<div className="flex-1 min-w-0">
					<SearchableSelect
						defaultMenuIsOpen
						autoFocus
						size="sm"
						value={currentId || UNASSIGNED_VALUE}
						onValueChange={(v) => pick(v || UNASSIGNED_VALUE)}
						onMenuClose={() => setEditing(false)}
						options={options}
						placeholder="Unassigned"
						renderOption={(opt) => (
							<div className="flex items-center gap-2">
								<div>
									<div className="text-xs font-medium">
										{opt.label}
									</div>
									{opt.description && (
										<div className="text-[8px] text-muted truncate overflow-hidden line-clamp-1">
											{String(opt.description).split("@")[0]}
										</div>
									)}
								</div>
							</div>
						)}
					/>
				</div>
				{saving && (
					<Loader2 className="h-3.5 w-3.5 animate-spin text-muted" />
				)}
			</div>
			{additionalSection}
		</div>
	);
}
